import { map, first } from 'rxjs/operators';
import rfdc from 'rfdc';
import { select, Store } from '@ngrx/store';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import * as _ from 'lodash';
import { Observable, BehaviorSubject } from 'rxjs';
import moment from 'moment';
import { FieldActivity } from '../_models';
import { Equipment } from '../_models';
import { environment } from '../../../environments/environment';
import { EquipmentWarning } from '../_models/equipmentWarning';
import { AppState } from '../_reducers';
import { selectAllEquipments } from '../_selectors/equipment.selectors';
import { selectAllFieldActivities } from '../_selectors/fieldActivity.selectors';

@Injectable()
export class EquipmentService {
  private clone = rfdc();

  private apiBaseUrl = environment.apiBaseUrl;
  private fieldActivityList: Observable<FieldActivity[]>;
  private equipmentList: Observable<Equipment[]>;
  equipmentTemplateList = new BehaviorSubject<Equipment[]>([]);
  selectedEquipmentForinCabReal$: BehaviorSubject<Equipment> = new BehaviorSubject<Equipment>(null);
  equipmentWithToolConfig: Map<any, Array<any>> = new Map();
  mapForDescriptionSignalInToolConfig: Map<any, Array<any>> = new Map();
  mapForUMTolerance: Map<any, Array<any>> = new Map();

  constructor(
    private http: HttpClient,
    private store: Store<AppState>
  ) {
    this.equipmentList = this.store.pipe(select(selectAllEquipments));
  }

  getFromLocalStore(): Observable<Equipment[]> {
    return this.equipmentList;
  }

  getConnectedEquipments = (): Equipment[] => {
    let equipments: Equipment[];
    this.equipmentList.subscribe((eqs) => {
      equipments = _.filter(eqs, (eq) => {
        return eq.externalResources && eq.externalResources.length > 0;
      });
    });
    return equipments;
  };

  getEquipments(): Observable<Equipment[]> {
    return this.http
      .get<any>(this.apiBaseUrl + 'api/private/v1/equipments')
      .pipe(map((response) => response));
  }
  getEquipment(id: number): Observable<Equipment> {
    return this.http
      .get<any>(this.apiBaseUrl + 'api/private/v1/equipments/' + id)
      .pipe(map((response) => response));
  }

  saveEquipment(equipmentItem): Observable<Equipment> {
    const equipment = { ...equipmentItem };

    delete equipment.lastUpdated;
    delete equipment.dateCreated;
    delete equipment.activities;
    delete equipment.lastSyncDate;
    delete equipment.lastSyncLat;
    delete equipment.lastSyncLng;

    const dateFormatted = moment(equipment.matriculationDate).startOf('day').utc().toISOString();
    equipment.matriculationDate = dateFormatted ? dateFormatted.replace(':00Z', ':00.000Z') : null;

    if (equipment.manager) {
      const idmanager: any = {
        id: equipment.manager,
      };
      equipment.manager = idmanager;
    }

    const companySelected = JSON.parse(localStorage.getItem('companySelected'));
    equipment.companyId = companySelected ? companySelected.id : null;

    if (equipment.id) {
      return this.http
        .put<Equipment>(this.apiBaseUrl + 'api/private/v1/equipments/' + equipment.id, equipment)
        .pipe(map((response) => response));
    } else {
      return this.http
        .post<Equipment>(this.apiBaseUrl + 'api/private/v1/equipments/', equipment)
        .pipe(map((response) => response));
    }
  }

  deleteEquipment(id: number): Observable<any> {
    return this.http.delete(this.apiBaseUrl + 'api/private/v1/equipments/' + id).pipe(
      map((response) => {
        return { id } as Equipment;
      })
    );
  }

  isEquipmentInActivities(equipment: Equipment): Observable<boolean> {
    let belongsToActivities: Observable<boolean>;
    this.fieldActivityList = this.store.pipe(select(selectAllFieldActivities));

    const equipmentId = equipment.id;

    const subFieldActivityList = this.fieldActivityList.subscribe((fieldActivities) => {
      belongsToActivities = new Observable((observer) => {
        const findInActivities =
          _.findIndex(fieldActivities, (fieldActivity: FieldActivity) => {
            const eIndex = _.findIndex(fieldActivity.equipments, (e: Equipment) => {
              return e.id === equipmentId;
            });
            return eIndex !== -1;
          }) !== -1;

        observer.next(findInActivities);
        observer.complete();
      });
    });
    return belongsToActivities;
  }

  equipmentListByLocation = (equipmentList: Array<Equipment>, dataSimplify = false) => {
    const list = [];
    const tmpArray = [];
    let localMarker = null;
    equipmentList.forEach((equipment) => {
      if (equipment.lastSyncLat && equipment.lastSyncLng) {
        const tmpElement: any = {};
        tmpElement.iconUrl = [];
        tmpElement.data = [];
        let posNotPresent: boolean = false;
        for (let i = 0; !posNotPresent; i += 0.00004) {
          tmpElement.lastSyncLat = +equipment.lastSyncLat + i;
          tmpElement.lastSyncLng = +equipment.lastSyncLng + i;
          if (!tmpArray.includes(tmpElement.lastSyncLat + '-' + tmpElement.lastSyncLng)) {
            tmpArray.push(tmpElement.lastSyncLat + '-' + tmpElement.lastSyncLng);
            posNotPresent = true;
          }
        }

        if (equipment.isOn) {
          localMarker = this.isTractorType(equipment.type)
            ? 'assets/images/tractorOn.png'
            : 'assets/images/frontToolOn.png';
        } else {
          localMarker = this.isTractorType(equipment.type)
            ? 'assets/images/tractorOff.png'
            : 'assets/images/frontToolOff.png';
        }
        const tmpImg = {
          url: equipment.imgUrl ? equipment.imgUrl : localMarker,
          scaledSize: {
            height: 50,
            width: 50,
          },
        };
        tmpElement.iconUrl.push(tmpImg);
        if (dataSimplify) {
          tmpElement.data.push({ id: equipment.id, isOn: equipment.isOn } as any);
        } else {
          tmpElement.data.push(equipment);
        }
        list.push(tmpElement);
      }
    });
    return list;
  };

  getEquipmentsOPModule(farmsAppend): Observable<Equipment[]> {
    return this.http
      .get<any>(this.apiBaseUrl + 'api/private/v1/equipments/' + farmsAppend)
      .pipe(map((response) => response));
  }

  saveEquipmentDevice(
    equipment,
    operation: string,
    type: string,
    externalSource = null,
    externalSerialNumber = null,
    defaultProvider = null,
    externalId = null
  ): Observable<Equipment> {
    delete equipment.lastUpdated;
    delete equipment.dateCreated;

    const companySelected = JSON.parse(localStorage.getItem('companySelected'));
    equipment.companyId = companySelected ? companySelected.id : null;

    const dateFormatted = moment(equipment.matriculationDate).startOf('day').utc().toISOString();
    equipment.matriculationDate = dateFormatted ? dateFormatted.replace(':00Z', ':00.000Z') : null;

    return this.http
      .put<Equipment>(
        this.apiBaseUrl +
          'api/private/v1/equipmentDevices/' +
          equipment.id +
          '?operation=' +
          operation +
          '&type=' +
          type +
          (externalSource != null ? '&externalSource=' + externalSource : '') +
          (externalSerialNumber != null ? '&externalSerialNumber=' + externalSerialNumber : '') +
          (defaultProvider != null ? '&defaultProvider=' + defaultProvider : '') +
          (externalId != null ? '&externalId=' + externalId : ''),
        equipment
      )
      .pipe(map((response) => response));
  }

  getEquipmentById = (id: number): Equipment => {
    let item: Equipment;
    this.equipmentList
      .pipe(first((list: Equipment[]) => list && list.length > 0))
      .subscribe((list: Equipment[]) => {
        item = list.find((i) => i.id === id);
      });
    return item;
  };

  getEquipmentsTemplate(saasCompanyCode: string): Observable<Equipment[]> {
    return this.http
      .get<any>(
        this.apiBaseUrl + 'api/private/v1/equipmentTemplates?saasCompanyCode=' + saasCompanyCode
      )
      .pipe(
        map((response) => {
          if (response) {
            if (response) {
              this.equipmentTemplateList.next(response);
              return response;
            }
          }
        })
      );
  }

  getEquipmentByFrameIdAndSaasCompanyCode(
    frameId: string,
    saasCompanyCode: string
  ): Observable<Equipment> {
    const address =
      this.apiBaseUrl +
      `
api/private/v1/equipmentTemplates
?frameId=${frameId}
&saasCompanyCode=${saasCompanyCode}
&isEquipmentFrameQuery=true
`;

    return this.http.get<any>(address).pipe(
      map((response) => {
        if (response) {
          if (response) {
            return response[0];
            // !!! - BE returns list<EquipmentTemplate> that gets mapped to an `Equipment`  (fix this?)
            // Is it worth reading everything into an `EquipmentTemplate` and then mapping (or casting?) result to `Equipment` ?
          }
        }
      })
    );
  }

  getTemplateList(): Observable<Equipment[]> {
    return this.equipmentTemplateList;
  }

  tractorType() {
    return [
      'tractor',
      'straddle_tractor_1_rows',
      'straddle_tractor_2_rows',
      'harvesting_machine',
      'tracked_tractor',
      'fertilizer_self_propelled',
      'mower_self_propelled',
      'seeder_self_propelled',
      'mixer_wagon_self_propelled',
    ];
  }

  isTractorType(parData): boolean {
    return this.tractorType().includes(parData);
  }

  createEquipmentMapsWithToolConfig() {
    this.equipmentList.subscribe((list) => {
      if (list && list.length > 0) {
        this.equipmentWithToolConfig.clear();
        this.mapForDescriptionSignalInToolConfig.clear();
        (this, this.mapForUMTolerance.clear());
        list.forEach((eq) => {
          if (eq && eq.toolConfiguration) {
            const tmpToolConf = this.clone(eq.toolConfiguration);
            tmpToolConf.id = eq.toolConfiguration.id.toString().concat('|').concat(eq.id);
            this.equipmentWithToolConfig.set(eq.id, tmpToolConf);
            if (eq.toolConfiguration.toolConfig && eq.toolConfiguration.toolConfig.length > 0) {
              eq.toolConfiguration.toolConfig.forEach((toolConfig) => {
                if (toolConfig && toolConfig.signals && toolConfig.signals.length > 0) {
                  toolConfig.signals.forEach((signal) => {
                    if (signal && signal.is_target && signal.is_target == true) {
                      this.mapForDescriptionSignalInToolConfig.set(
                        signal.signal_id,
                        signal.signal_desc
                      );
                    }
                  });
                }
              });
            }
          }
        });
      }
    });
  }

  getToolConfigurationByEquipmentId(id) {
    return this.equipmentWithToolConfig.get(id);
  }

  getDescriptionSignalToolBySignalId(id) {
    return this.mapForDescriptionSignalInToolConfig.get(id);
  }

  getUnitOfMeausureToleranceBySignalId(eqId, signalId): string {
    const tmp = this.equipmentWithToolConfig.get(eqId) as any;
    let currentUm = null;
    if (tmp.toolConfig && tmp.toolConfig.length > 0) {
      tmp.toolConfig.forEach((item) => {
        if (item.signals && item.signals.length > 0) {
          item.signals.forEach((elem) => {
            if (elem.signal_id == signalId) {
              currentUm = elem.signal_um;
            }
          });
        }
      });
    }

    return currentUm;
  }

  getEquipmentsWarning(): Observable<EquipmentWarning[]> {
    return this.http
      .get<any>(this.apiBaseUrl + 'api/private/v1/equipmentWarningPrivate')
      .pipe(map((response) => response));
  }

  getFieldActivitiesKpi(year: number): Observable<any> {
    return this.http
      .get<any>(this.apiBaseUrl + 'api/private/v1/fieldActivitiesKpi?cropYear=' + year)
      .pipe(map((response) => response));
  }
}
